import { useCallback, useEffect, useState } from 'react';
import { SxStyle } from '../../../../../types';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import SquadDialog, { SquadDialogProps } from '../squad-dialog';
import DialogContent from '@mui/material/DialogContent';
import { useStringsContext } from '../../../../../contexts/strings';
import DialogActions from '@mui/material/DialogActions';
import { SquadPlayer } from '../../types';
import DialogContentText from '@mui/material/DialogContentText';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import PlayerAvatar from '../../../../common/player-avatar';
import PlayerSelect from './player-select';
import { usePlayer } from '../../hooks/player';
import { Check } from '@mui/icons-material';
import ImageDropzone, {
  FileRejectionWithDimensions,
  FileWithDimensions,
} from '../../../../common/image-dropzone';

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const MIN_IMAGE_DIMENSION = 800;
const MAX_IMAGE_DIMENSION = 2000;
const MIN_IMAGE_ERROR = 'image-too-small';
const MAX_IMAGE_ERROR = 'image-too-big';
const MAX_SIZE_ERROR = 'file-too-large';

interface Props extends Pick<SquadDialogProps, 'open' | 'onClose'> {
  onSubmitted: () => void;
  selectedSquad: SquadPlayer[];
  selectedSeason: string;
}

const styles: SxStyle = {
  subTitle: {
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingBottom: '16px',
  },
  contentList: {
    padding: '16px',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100px',
  },
  tableRow: (theme) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }),
  dropzone: {
    minHeight: '200px',
    padding: '20px',
  },
  dropzoneText: {
    fontSize: '1rem',
  },
};

function imageSizeValidator(file: FileWithDimensions) {
  if (file.width < MIN_IMAGE_DIMENSION || file.height < MIN_IMAGE_DIMENSION) {
    return {
      code: MIN_IMAGE_ERROR,
      message: `Bilden är för liten (${file.width}x${file.height} px)`,
    };
  } else if (file.width > MAX_IMAGE_DIMENSION || file.height > MAX_IMAGE_DIMENSION) {
    return {
      code: MAX_IMAGE_ERROR,
      message: `Bilden är för stor (${file.width}x${file.height} px)`,
    };
  }
  return null;
}

const getErrorMessage = (file: FileWithDimensions, code: string, message: string) => {
  return code === MAX_SIZE_ERROR ? `Bilden har för stor filstorlek (${file.size} bytes)` : message;
};

export default function UploadPlayerImagesModal({
  open,
  onClose,
  onSubmitted,
  selectedSquad,
  selectedSeason,
}: Props) {
  const { strings } = useStringsContext();
  const { uploadPlayerImage } = usePlayer();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);

  const [playerImageMapping, setPlayerImageMapping] = useState<
    {
      player: SquadPlayer | null;
      file: File;
      status: string | null;
      error?: string;
    }[]
  >([]);

  useEffect(() => {
    setSubmitDisabled(
      playerImageMapping.every((m) => !m.player || m.status) ||
        playerImageMapping.some(
          (m) => m.player && playerImageMapping.filter((m2) => m2.player === m.player).length > 1
        )
    );
  }, [playerImageMapping]);

  const onSubmit = useCallback(async () => {
    setSubmitLoading(true);
    try {
      let success = false;
      for (const { player, file, status, error } of playerImageMapping) {
        if (player && file && !status && !error) {
          setPlayerImageMapping((prev) =>
            prev.map((m) => (m.player === player ? { ...m, status: 'uploading' } : m))
          );
          try {
            await uploadPlayerImage(player, file, selectedSeason);
            success = true;
            setPlayerImageMapping((prev) =>
              prev.map((m) => (m.player === player ? { ...m, status: 'uploaded' } : m))
            );
          } catch (e) {
            setPlayerImageMapping((prev) =>
              prev.map((m) =>
                m.player === player ? { ...m, status: 'error', error: strings.messages.error } : m
              )
            );
          }
        }
      }
      if (success) {
        onSubmitted?.();
      }
    } finally {
      setSubmitLoading(false);
    }
  }, [playerImageMapping, onSubmitted, uploadPlayerImage, strings]);

  const onImageAdded = useCallback(
    (files: FileWithDimensions[]) => {
      setPlayerImageMapping((prev) => [
        ...prev,
        ...files.map((file) => {
          const image = file;

          return {
            player:
              selectedSquad.find(
                (player) =>
                  image.name.includes(player.clId) ||
                  image.name.includes(String(player.fogisId)) ||
                  (player.smcId && image.name.includes(player.smcId))
              ) || null,
            file: image,
            status: null,
            error: undefined,
          };
        }),
      ]);
    },
    [setPlayerImageMapping, selectedSquad]
  );

  const onImageRejected = useCallback(
    (files: FileRejectionWithDimensions[]) => {
      setPlayerImageMapping((prev) => [
        ...prev,
        ...files.map((file) => {
          const image = file.file;
          return {
            player:
              selectedSquad.find(
                (player) =>
                  image.name.includes(player.clId) ||
                  image.name.includes(String(player.fogisId)) ||
                  (player.smcId && image.name.includes(player.smcId))
              ) || null,
            file: image,
            status: file.errors?.[0].code ? 'error' : null,
            error:
              getErrorMessage(image, file.errors?.[0].code, file.errors?.[0].message) || undefined,
          };
        }),
      ]);
    },
    [setPlayerImageMapping, selectedSquad]
  );

  const onRemoveImage = useCallback(
    (index: number) => {
      setPlayerImageMapping((prev) => prev.filter((m, i) => index !== i));
    },
    [setPlayerImageMapping]
  );

  return (
    <SquadDialog
      open={open}
      onClose={onClose}
      title={strings.squad.uploadSquadImages}
      maxWidth="md"
    >
      <>
        <DialogContentText sx={styles.subTitle}>
          <Typography variant="italic">{strings.squad.uploadPlayerImagesInfo}</Typography>
        </DialogContentText>
        <DialogContent sx={styles.contentList} dividers>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <ImageDropzone
                onValidator={imageSizeValidator}
                onAcceptedFiles={onImageAdded}
                onRejectedFiles={onImageRejected}
                maxFiles={999}
                maxSize={MAX_FILE_SIZE}
              />
            </Grid>
            <Grid item xs={6}>
              <h3>Krav på bild</h3>
              <ol>
                <li>{`Storlek/mått: Kvadrat - minst ${MIN_IMAGE_DIMENSION}x${MIN_IMAGE_DIMENSION} px och max ${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION} px`}</li>
                <li>Frilagd spelare på transparent bakgrund</li>
                <li>Friyta ovanför huvudet enligt exempel</li>
                <li>Spelaren i bild, från bröst till topp, se exempel</li>
                <li>Inga textelement eller annat än rena spelarbilder</li>
              </ol>
            </Grid>
            <Grid item xs={2}>
              <h3>Exempel</h3>
              <PlayerAvatar srcImage="/avatar.png" size="large" variant="square" showTransparency />
            </Grid>
            <Grid item xs={12}>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {playerImageMapping?.map(({ player, file, status, error }, i) => (
                      <TableRow key={i} sx={styles.tableRow}>
                        <TableCell style={{ width: '100px' }}>
                          <PlayerAvatar
                            srcImage={URL.createObjectURL(file)}
                            size="large"
                            showTransparency
                          />
                        </TableCell>
                        <TableCell>
                          {!status ? (
                            <PlayerSelect
                              selectedSquad={selectedSquad}
                              isLoading={false}
                              player={player}
                              onSelectedPlayer={(selectedPlayer) => {
                                const newPlayerImageMapping = [...playerImageMapping];
                                newPlayerImageMapping[i].player = selectedPlayer;
                                setPlayerImageMapping(newPlayerImageMapping);
                              }}
                            />
                          ) : (
                            <Typography color="error" mt={2}>
                              {error}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell style={{ width: '100px' }}>
                          {status === 'uploading' && <CircularProgress />}
                          {status === 'uploaded' && <Check color="success" />}
                          {(!status || status === 'error') && (
                            <IconButton onClick={() => onRemoveImage(i)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
      </>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {strings.actions.close}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          disabled={submitDisabled}
          loading={submitLoading}
          onClick={onSubmit}
        >
          {strings.actions.upload}
        </LoadingButton>
      </DialogActions>
    </SquadDialog>
  );
}
